/* eslint react/no-unused-prop-types: "off" */

import React from 'react'
import _ from 'lodash'
import { PoseGroup } from 'react-pose'
import { Col } from 'react-styled-flexboxgrid'
import numeral from 'numeral'
import PropTypes from 'prop-types'
import { calculatePrice, DailyUsage, QUERY_PRICE_RANGES, DATA_PRICE_RANGES } from '@aitodotai/aito-price-tool'
import { Slider, MediaQuery } from '..'
import * as styles from './styles'
import variables from '../../styles/variables'

const propTypes = {
  background: PropTypes.string,
  data: PropTypes.object.isRequired
}

const defaultProps = {
  background: ''
}

const dataRanges = [
  { start: 0, end: 1, rangeText: '0 - 1GB' },
  { start: 1, end: 2, rangeText: '1 - 2GB' },
  { start: 2, end: 4, rangeText: '2 - 4GB' },
  { start: 4, end: 8, rangeText: '4 - 8GB' },
  { start: 8, end: 16, rangeText: '8 - 16GB' },
  { start: 16, end: 1024, rangeText: '16GB - 1TB' },
]

const dataMarks = {
  0: '',
  1: '',
  2: '',
  3: '',
  4: '',
  5: '',
  6: '',
}

function sliderValueToGb(val) {
  const index = val >= dataRanges.length - 1 ? dataRanges.length - 1 : Math.floor(val)
  const decimals = val - index

  const { start, end } = dataRanges[index]
  return (start + (end - start) * decimals)
}

const queryRanges = [
  { start: 0, end: 10 ** 3, rangeText: '0 - 1000' },
  { start: 10 ** 3, end: 10 ** 4, rangeText: '1000 - 10k' },
  { start: 10 ** 4, end: 10 ** 5, rangeText: '10k - 100k' },
  { start: 10 ** 5, end: 10 ** 6, rangeText: '100k - 1M' },
  { start: 10 ** 6, end: 10 ** 7, rangeText: '1M - 10M' },
  { start: 10 ** 7, end: 10 ** 8, rangeText: '10M+' },
]

const queryMarks = {
  0: '',
  1: '',
  2: '',
  3: '',
  4: '',
  5: '',
  6: '',
}

function sliderValueToQueries(val) {
  const index = val >= queryRanges.length - 1 ? queryRanges.length - 1 : Math.floor(val)
  const decimals = val - index

  const { start, end } = queryRanges[index]
  return (start + (end - start) * decimals)
}

function getDataRangePricesPerDay() {
  return _.take(DATA_PRICE_RANGES, dataRanges.length)
}

function getQueryRangePrices() {
  return _.take(QUERY_PRICE_RANGES, queryRanges.length)
}

function getDataRangePriceLabels(timePeriod) {
  const prices = getDataRangePricesPerDay()
  return _.map(prices, (dayPrice, index) => {
    const left = `${((index + 0.5) / prices.length) * 100}%`
    const plusLeft = `${index / prices.length * 100}%`

    if (timePeriod === 'day') {
      return {
        rangeText: dataRanges[index].rangeText,
        priceText: index === 0 ? 'Min. 6 €' : `${dayPrice} € / GB`,
        left,
        plusLeft,
      }
    }

    return {
      rangeText: dataRanges[index].rangeText,
      priceText: index === 0 ? '6 € / day' : `${dayPrice} € / GB / day`,
      left,
      plusLeft,
    }
  })
}

function getQueryRangePriceLabels(timePeriod) {
  const scaledRanges = timePeriod === 'day'
    ? _.map(queryRanges, (r, index) => {
      const newStart = Math.round(r.start / 30)
      const newEnd = Math.round(r.end / 30)

      const text = index === queryRanges.length - 1
        ? `${numeral(newStart).format('0a')}+`
        : `${numeral(newStart).format('0a')} - ${numeral(newEnd).format('0a')}`

      return _.merge({}, r, {
        start: newStart,
        end: newEnd,
        rangeText: text,
      })
    })
    : queryRanges

  const prices = getQueryRangePrices()
  return _.map(prices, (price, index) => {
    const left = `${(index + 0.5) / prices.length * 100}%`
    const plusLeft = `${index / prices.length * 100}%`

    if (price <= 0) {
      return { rangeText: scaledRanges[index].rangeText, priceText: 'Free', left, plusLeft }
    }

    return {
      rangeText: scaledRanges[index].rangeText,
      priceText: `${numeral(price * 100).format('0.[0000]')} ¢ / req`,
      left,
      plusLeft,
    }
  })
}

const SliderLabels = (props) => {
  const { labels, sliderValue } = props

  return (
    <styles.SliderLabels>
      {
        _.map(labels, (label, index) => {
          const active = sliderValue > index
          const plus = (
            <MediaQuery query={variables.media.sm}>
              <styles.SliderLabelRangePlus left={label.plusLeft} active={active}>
                +
              </styles.SliderLabelRangePlus>
            </MediaQuery>
          )
          return (
            <li key={index}>
              {index === 0 ? null : plus}

              <styles.SliderLabel left={label.left}>
                <styles.SliderLabelRange active={active}>
                  {label.rangeText}
                </styles.SliderLabelRange>
                <MediaQuery query={variables.media.sm}>
                  <styles.SliderLabelRangePrice active={active}>
                    {label.priceText}
                  </styles.SliderLabelRangePrice>
                </MediaQuery>
              </styles.SliderLabel>
            </li>
          )
        })
      }
    </styles.SliderLabels>
  )
}

SliderLabels.propTypes = {
  labels: PropTypes.array.isRequired,
  sliderValue: PropTypes.number.isRequired
}

export default class PricingCalculator extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      queriesSliderValue: 1,
      dataSliderValue: 1,
      showPriceForPeriod: 'month' // or 'month'
    }
  }

  onDataChange = (val) => {
    this.setState({ dataSliderValue: val })
  }

  onQueriesChange = (val) => {
    this.setState({ queriesSliderValue: val })
  }

  render() {
    const { queriesSliderValue, dataSliderValue, showPriceForPeriod } = this.state

    const queriesCount = sliderValueToQueries(queriesSliderValue)
    const dataAmount = sliderValueToGb(dataSliderValue)
    const numberOfDays = showPriceForPeriod === 'day' ? 1 : 30
    const du = new DailyUsage(dataAmount, 0)

    const simulatedUsage = _.map(_.range(numberOfDays - 1), i => i).map(() => du)

    simulatedUsage.push(new DailyUsage(dataAmount, queriesCount))

    const totalPrice = calculatePrice(simulatedUsage)
    const showContactUs = queriesSliderValue > queryRanges.length - 1
      || dataSliderValue > dataRanges.length - 1

    const { data } = this.props

    return (
      <styles.Container>
        <styles.Header>{data.header}</styles.Header>

        <styles.FirstSliderRow>
          <styles.FirstCol sm={2} xs={12}>
            <styles.PricingComponentLabel>Amount of data</styles.PricingComponentLabel>
          </styles.FirstCol>

          <styles.SliderCol sm={10} xs={12}>
            <styles.DataAmountSlider>
              <Slider
                onChange={this.onDataChange}
                value={dataSliderValue}
                min={0}
                max={6}
                marks={dataMarks}
                step={0.1}
                tipProps={{ visible: true }}
                tipFormatter={(val) => {
                  if (dataSliderValue > dataRanges.length - 1) {
                    return _.last(dataRanges).rangeText
                  }

                  return `${sliderValueToGb(val).toFixed(1)} GB`
                }}
              />
              <SliderLabels
                labels={getDataRangePriceLabels(showPriceForPeriod)}
                sliderValue={dataSliderValue}
              />
            </styles.DataAmountSlider>
          </styles.SliderCol>
        </styles.FirstSliderRow>

        <styles.SliderRow>
          <styles.FirstCol sm={2} xs={12}>
            <styles.PricingComponentLabel>
              Queries per
              {' '}
              {showPriceForPeriod}
            </styles.PricingComponentLabel>
          </styles.FirstCol>
          <styles.SliderCol sm={10} xs={12}>
            <styles.QueriesSlider>
              <Slider
                onChange={this.onQueriesChange}
                value={queriesSliderValue}
                min={0}
                max={6}
                marks={queryMarks}
                step={0.1}
                tipProps={{ visible: true }}
                tipFormatter={(val) => {
                  if (queriesSliderValue > queryRanges.length - 1) {
                    return _.last(queryRanges).rangeText
                  }
                  return `${numeral(sliderValueToQueries(val)).format()}`
                }}
              />
              <SliderLabels
                labels={getQueryRangePriceLabels(showPriceForPeriod)}
                sliderValue={queriesSliderValue}
              />
            </styles.QueriesSlider>
          </styles.SliderCol>
        </styles.SliderRow>

        <styles.SliderRow>
          <styles.FirstCol sm={2} xs={12}>
            <p>Estimated cost</p>
          </styles.FirstCol>
          <Col sm={10} xs={12}>
            <styles.Price>
              <styles.PriceSlot>
                <PoseGroup>
                  {
                    showContactUs
                      ? (
                        <styles.ContactLink key="contact" href="mailto:hello@aito.ai">
                          <styles.ContactButton>Contact sales</styles.ContactButton>
                        </styles.ContactLink>
                      )
                      : (
                        <styles.PriceNumber key="price">
                          {totalPrice.toFixed(2)}
                          {' '}

                          <styles.PricePerPeriod>
                            &euro; /
                            {' '}
                            {showPriceForPeriod === 'month' ? 'mo' : showPriceForPeriod}
                          </styles.PricePerPeriod>
                        </styles.PriceNumber>
                      )
                  }
                </PoseGroup>
              </styles.PriceSlot>

              <styles.PriceExplanation>
                {data.info1}
                <br />
                {data.info2}
              </styles.PriceExplanation>
            </styles.Price>
          </Col>
        </styles.SliderRow>
      </styles.Container>
    )
  }
}

PricingCalculator.propTypes = propTypes
PricingCalculator.defaultProps = defaultProps
